import React, { useCallback, useRef, useState, useEffect } from 'react'
//MUI
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import SvgIcon from '@mui/material/SvgIcon'
import Typography from '@mui/material/Typography'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined'
import FormatColorTextIcon from '@mui/icons-material/FormatColorText'
import TitleIcon from '@mui/icons-material/Title'
import CallToActionIcon from '@mui/icons-material/CallToAction'
import ColorLensIcon from '@mui/icons-material/ColorLens'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay'
import ImageIcon from '@mui/icons-material/Image'
import FormatButton from './FormatButton'
import imageConverter from '../../helpers/imageConverter'
import AddLinkIcon from '@mui/icons-material/AddLink'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import { Modal } from '@mui/material'
import API from '../../services/API'
import axios from 'axios'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import CodeIcon from '@mui/icons-material/Code'
import BorderColorIcon from '@mui/icons-material/BorderColor' // highlight

const ControlPanel = ({ editor, setLink, isCRM }) => {
  const [headingAnchorEl, setHeadingAnchorEl] = useState(null)
  const [alertAnchorEl, setAlertAnchorEl] = useState(null)
  const imageRef = useRef()
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')

  // Upload handler (edit mode)
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      // 1. Get signed upload URL + key
      const res = await API.post('/kbdocs/image-upload-url', {
        filename: file.name,
        extension: file.name.split('.').slice(-1)[0],
      })

      const uploadUrl = res.data.url
      const key = res.data.key

      // 2. Upload to S3
      await API.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
      })

      // 3. Get signed GET URL for display
      const getUrlRes = await API.post('/kbdocs/image-download-url', {
        key,
        inlineView: true,
      })

      const signedUrl = getUrlRes.data

      editor.chain().focus().setImage({ src: signedUrl, 'data-key': key }).run()

      // Use MutationObserver to wait until image is in DOM, then patch it
      const observer = new MutationObserver(() => {
        const images = document.querySelectorAll(`img[src="${key}"]`)
        if (images.length > 0) {
          images.forEach((img) => img.setAttribute('src', signedUrl))
          observer.disconnect()
        }
      })

      // Observe changes in the editor DOM
      observer.observe(document.querySelector('.ProseMirror'), {
        childList: true,
        subtree: true,
      })
    } catch (err) {
      console.error('Image upload failed:', err)
      alert('Image upload failed. Please try again.')
    }
  }

  return (
    <Stack direction="row" alignItems="center">
      <IconButton
        sx={{
          borderRadius: 1,
          color: '#1F3D7C',
          backgroundColor: 'transparent',
        }}
        onMouseDown={(e) => {
          e.preventDefault()
          setHeadingAnchorEl(e.currentTarget)
        }}
      >
        <TitleIcon sx={{ color: '#1F3D7C' }} fontSize="small" />
      </IconButton>
      <FormatButton
        icon={FormatBoldIcon}
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
      />
      <FormatButton
        icon={FormatItalicIcon}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
      />
      <FormatButton
        icon={FormatUnderlinedIcon}
        size="small"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
      />
      <FormatButton
        icon={FormatQuoteIcon}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
      />

      <FormatButton
        icon={CodeIcon}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive('codeBlock')}
      />

      <FormatButton
        icon={BorderColorIcon}
        onClick={() => editor.chain().focus().toggleHighlight().run()}
        isActive={editor.isActive('highlight')}
      />
      {/* TODO: Allow Tenant Image Upload */}
      {isCRM && (
        <FormatButton
          icon={ImageIcon}
          onClick={() => imageRef.current.click()}
        />
      )}
      <FormatButton
        icon={AddLinkIcon}
        onClick={() => {
          setLinkUrl(editor.getAttributes('link').href || '')
          setLinkDialogOpen(true)
        }}
        isActive={editor.isActive('link')}
      />

      <FormatButton
        icon={LinkOffIcon}
        onClick={() => {
          editor.chain().focus().unsetLink().run()
          setLinkDialogOpen(false)
        }}
        isActive={editor.isActive('link')}
      />
      <IconButton
        sx={{
          borderRadius: 1,
          color: '#1F3D7C',
          backgroundColor: 'transparent',
        }}
        onMouseDown={(e) => {
          e.preventDefault()
          setAlertAnchorEl(e.currentTarget)
        }}
      >
        <CallToActionIcon sx={{ color: '#1F3D7C' }} fontSize="small" />
      </IconButton>
      <Modal open={linkDialogOpen}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            zIndex: 999,
            p: 2,
            bgcolor: 'background.paper',
            border: '1px solid #ccc',
            borderRadius: 1,
            boxShadow: 3,
          }}
        >
          <Stack direction="row" gap={1} alignItems="center">
            <input
              type="text"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              style={{ padding: '4px 8px', width: '250px' }}
            />
            <IconButton
              size="small"
              onClick={() => {
                editor
                  .chain()
                  .focus()
                  .extendMarkRange('link')
                  .setLink({ href: linkUrl })
                  .run()
                setLinkDialogOpen(false)
              }}
            >
              <AddLinkIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setLinkDialogOpen(false)}>
              <LinkOffIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      </Modal>
      <input
        ref={imageRef}
        type="file"
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleImageUpload}
      />
      <input
        type="color"
        onChange={(event) =>
          editor.chain().focus().setColor(event.target.value).run()
        }
        value={editor.getAttributes('textStyle').color || '#000000'}
      />
      <FormatButton
        icon={FormatListBulletedIcon}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
      />
      <FormatButton
        icon={FormatListNumberedIcon}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
      />

      <Menu
        open={!!headingAnchorEl}
        anchorEl={headingAnchorEl}
        onClose={() => setHeadingAnchorEl(null)}
      >
        {['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map((variant, index) => (
          <MenuItem
            key={variant}
            onClick={() => {
              editor
                .chain()
                .focus()
                .toggleHeading({ level: index + 1 })
                .run()
              setHeadingAnchorEl(null)
            }}
          >
            <Typography
              variant={variant}
              fontSize={`${2 - index * 0.2}rem`}
              color="inherit"
            >
              Heading {index + 1}
            </Typography>
          </MenuItem>
        ))}
        <MenuItem
          onClick={() => {
            editor.chain().focus().setParagraph().run()
            setHeadingAnchorEl(null)
          }}
        >
          <Typography variant="body1">Paragraph</Typography>
        </MenuItem>
      </Menu>
      <Menu
        open={!!alertAnchorEl}
        anchorEl={alertAnchorEl}
        onClose={() => setAlertAnchorEl(null)}
      >
        {['Info', 'Success', 'Error', 'Warning'].map((variant, index) => (
          <MenuItem
            key={variant}
            onClick={() => {
              editor.chain().focus().insertMuiAlert(variant.toLowerCase()).run()
              setAlertAnchorEl(null)
            }}
          >
            <Typography color={`${variant}.main`}>{variant}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  )
}

export default ControlPanel
